/**
 * Действия смены языка тренировки.
 *
 * Язык живёт в пространстве, а не в состоянии экрана: он переживает
 * перезагрузку так же, как прогресс, к которому относится.
 */

import { useCallback } from 'react';
import type { LanguageCode } from '@type2x/shared-contracts';
import type { Workspace } from '@type2x/feature-personal-workspace';
import type { OnboardingChoice } from '@type2x/feature-onboarding';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import { resumeScreen } from '../state/fundamentals.js';
import { startLanguage, switchTrainingLanguage } from '../state/language.js';

/** Действия, которые приложение передаёт экранам. */
export interface LanguageActions {
  /** Текущий язык тренировки; null — пространства ещё нет. */
  readonly trainingLanguage: LanguageCode | null;
  readonly onTrainingLanguageChange: (language: LanguageCode) => void;
  readonly onLanguageSetupDone: (choice: OnboardingChoice) => void;
  readonly onLanguageSetupCancel: () => void;
}

/** Что нужно смене языка от приложения. */
export interface LanguageDeps {
  readonly workspace: Workspace | null;
  readonly commit: (next: Workspace) => Promise<void>;
  /** Текущий экран: отказ от нового языка выглядит по-разному до и после него. */
  readonly screen: AppScreen;
  readonly setScreen: (screen: AppScreen) => void;
}

export function useLanguage(runtime: Runtime, deps: LanguageDeps): LanguageActions {
  const { workspace, commit, screen, setScreen } = deps;

  const onTrainingLanguageChange = useCallback(
    (language: LanguageCode) => {
      if (workspace === null) return;

      const transition = switchTrainingLanguage(runtime, workspace, language);
      if (transition === null) {
        // Выбран язык, который и так активен. Обычно делать нечего, но на
        // экране первого входа в другой язык это единственный способ сказать
        // «передумал»: профиль нового языка ещё не заведён, поэтому активным
        // остаётся прежний, и нажатие на него не меняло ничего — человек
        // оставался на экране, от которого отказывался (ТЗ FR-EN-87).
        if (screen.name === 'language-setup') setScreen(resumeScreen(runtime, workspace));
        return;
      }

      // Пространство сохраняется, только когда профиль языка уже есть: экран
      // первого входа в язык ничего не заводит, пока выбор не сделан.
      if (transition.workspace !== workspace) void commit(transition.workspace);
      setScreen(transition.screen);
    },
    [runtime, workspace, commit, screen, setScreen],
  );

  const onLanguageSetupDone = useCallback(
    (choice: OnboardingChoice) => {
      if (workspace === null) return;

      const transition = startLanguage(runtime, workspace, choice);
      void commit(transition.workspace);
      setScreen(transition.screen);
    },
    [runtime, workspace, commit, setScreen],
  );

  // Отказ от нового языка возвращает туда же, куда возвращает запуск
  // приложения: прежний язык остался нетронутым.
  const onLanguageSetupCancel = useCallback(() => {
    if (workspace === null) return;
    setScreen(resumeScreen(runtime, workspace));
  }, [runtime, workspace, setScreen]);

  return {
    trainingLanguage: workspace?.activeLanguage ?? null,
    onTrainingLanguageChange,
    onLanguageSetupDone,
    onLanguageSetupCancel,
  };
}
